import { Link } from "react-router-dom";
import { Page, Reveal, MaskedLine, Eyebrow, Marquee } from "@/components/motion/Reveal";
import { IMG } from "@/constants/images";

const ADVISORY = [
  "Business Strategy",
  "Operational Strategy",
  "Organizational Development",
  "Leadership Development",
  "Multi-Unit Operations",
  "Team Development",
  "Business Performance",
  "Growth Planning",
  "Systems and Processes",
  "Strategic Execution",
];

const BUILD = [
  { n: "01", title: "Build the Leader.", desc: "Strategy succeeds or stalls with the person leading it. We develop the leader first." },
  { n: "02", title: "Build the Team.", desc: "Resilient, high-performing teams built on clarity, trust, and standards." },
  { n: "03", title: "Build the Business.", desc: "Operations, systems, and structure positioned for sustainable growth." },
];

const PROCESS = [
  { n: "01", title: "Discover", desc: "We assess the business honestly: vision, operations, leadership, and market position." },
  { n: "02", title: "Design", desc: "We build the strategy: priorities, structure, systems, and a realistic roadmap." },
  { n: "03", title: "Build", desc: "We work alongside you to implement, developing leaders and teams as we go." },
  { n: "04", title: "Grow", desc: "We measure, refine, and position the business for its next stage." },
];

export default function BusinessDevelopment() {
  return (
    <Page data-testid="business-page">
      {/* HERO */}
      <section data-testid="business-hero" className="bg-obsidian text-ivory pt-40 pb-24 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative grid lg:grid-cols-12 gap-14 items-end">
          <div className="lg:col-span-8">
            <Eyebrow>Business Development · For the Business</Eyebrow>
            <h1 className="font-serif font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.08]">
              <MaskedLine delay={0.2}>Turn Vision into Strategy.</MaskedLine>
              <MaskedLine delay={0.38} className="italic text-gold">Turn Strategy into Growth.</MaskedLine>
            </h1>
            <Reveal delay={0.55}>
              <p className="text-base sm:text-lg font-light leading-relaxed text-ivory/55 mt-8 max-w-2xl">
                Genesis is a strategic partner for entrepreneurs and business owners who
                are starting, strengthening, growing, or repositioning a business.
              </p>
            </Reveal>
            <Reveal delay={0.65}>
              <Link
                to="/consultation"
                data-testid="business-hero-cta"
                className="inline-block mt-10 bg-gold text-obsidian px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
              >
                Book a Business Consultation
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.4}>
              <div className="border border-gold/30 p-8">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-4">Our Work, In One Line</p>
                <p className="font-serif text-xl leading-snug text-ivory/85">
                  Building Businesses. Developing Leaders. Strengthening Operations.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee items={["Building Businesses", "Developing Leaders", "Strengthening Operations"]} />

      {/* ADVISORY GRID */}
      <section data-testid="advisory-section" className="bg-ivory py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>Areas of Support</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-obsidian mt-8 max-w-2xl leading-tight">
              Where strategy <span className="italic text-gold-dark">takes shape.</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-obsidian/10 border border-obsidian/10 mt-16">
            {ADVISORY.map((a, i) => (
              <Reveal key={a} delay={i * 0.04} className="bg-ivory">
                <div className="p-10 h-full hover:bg-cream transition-colors duration-500" data-testid={`advisory-card-${i}`}>
                  <span className="font-mono text-[10px] text-gold tracking-[0.3em]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-xl text-obsidian mt-4">{a}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BUILD THE LEADER / TEAM / BUSINESS */}
      <section data-testid="build-section" className="bg-obsidian text-ivory py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>The Genesis Approach to Growth</Eyebrow></Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-14">
            {BUILD.map((b, i) => (
              <Reveal key={b.n} delay={i * 0.1} className="bg-obsidian">
                <div className="p-10 lg:p-12 h-full hover:bg-charcoal transition-colors duration-500" data-testid={`build-card-${b.n}`}>
                  <span className="font-serif text-5xl text-gold/30">{b.n}</span>
                  <h3 className="font-serif text-2xl lg:text-3xl mt-6">{b.title}</h3>
                  <p className="text-sm font-light text-ivory/55 mt-4 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="process-section" className="bg-cream py-24 lg:py-32 border-y border-obsidian/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>The Strategic Process</Eyebrow></Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-12 items-start">
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-obsidian/10 border border-obsidian/10">
              {PROCESS.map((p, i) => (
                <Reveal key={p.n} delay={i * 0.08} className="bg-cream">
                  <div className="p-10 h-full" data-testid={`process-step-${p.n}`}>
                    <span className="font-serif text-4xl text-gold/60">{p.n}</span>
                    <h3 className="font-serif text-2xl text-obsidian mt-4">{p.title}</h3>
                    <p className="text-sm font-light text-stone-text mt-3 leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.2}>
                <div className="relative">
                  <div className="absolute -inset-3 border border-gold/40 translate-x-3 translate-y-3 pointer-events-none" />
                  <img src={IMG.strategy} alt="Strategy session, vision into action" data-testid="business-strategy-image" className="relative w-full aspect-[4/5] object-cover" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section data-testid="business-promise-section" className="bg-black text-ivory py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-snug">
              Turn your vision into strategy, <span className="italic text-gold">and your strategy into action.</span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/consultation"
              data-testid="business-cta-book-consultation"
              className="inline-block mt-12 bg-gold text-obsidian px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
            >
              Book a Business Consultation
            </Link>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
